/* eslint-disable */

import { Camera2d } from './camera';
import { arrayBufferMemcpy } from './util';
import { Observable, Subscription, ReplaySubject } from '@reactivex/rxjs';
import { each, zip, zipObject, values, max, uniq, omit, extend, flatten } from 'lodash';
const _ = { each, zip, zipObject, values, max, uniq, omit, extend, flatten };

// const debug =           (...args) => {};
// const debugRenderItem = (...args) => {};
// import { sprintf as printf } from 'sprintf-js';
// const debug =           (...args) => console.log(`          StreamGL:renderer:`, printf(...args));
// const debugRenderItem = (...args) => console.log(`        StreamGL:renderItem:`, printf(...args));

////////////////////////////////////////////////////////////////////////////////
// Exports
////////////////////////////////////////////////////////////////////////////////

//config * canvas
export function init(config, canvas, urlParams) {
    /** @module Renderer */

    ////////////////////////////////////////////////////////////////////////////////
    // Globals across renderer instances
    ////////////////////////////////////////////////////////////////////////////////

    /** Cached dictionary of program.attribute: attribute locations
     * @type {Object.<string, Object.<string, GLint>>} */
    const attrLocations = {};

    /** Cached dictionary of program.uniform: uniform locations
     * @type {Object.<string, Object.<string, GLint>>} */
    const uniformLocations = {};

    let lastRenderTarget = {};
    let enabledVertexAttribArrayCache = [];

    /**
     * Wraps gl.getAttribLocation and caches the result, returning the cached result on subsequent
     * calls for the same attribute in the same program.
     * @param {WebGLRenderingContext} gl - the WebGL context
     * @param {WebGLProgram} program - the program the attribute is part of
     * @param {string} programName - the name of the program
     * @param {string} attribute - the name of the attribute in the shader source code
     */

    const getAttribLocationFast = addressMemoizer(attrLocations, 'attribute', 'getAttribLocation');

    /**
     * Wraps gl.getUniformLocation and caches the result, returning the cached result on subsequent
     * calls for the same uniform in the same program.
     * @param {WebGLRenderingContext} gl - the WebGL context
     * @param {WebGLProgram} program - the program the attribute is part of
     * @param {string} programName - the name of the program
     * @param {string} uniform - the name of the uniform in the shader source code
     */
    const getUniformLocationFast = addressMemoizer(
        uniformLocations,
        'uniform',
        'getUniformLocation'
    );

    /** The program currently in use by GL
     * @type {?WebGLProgram} */
    let activeProgram = null;

    /** The currently bound buffer in GL
     * @type {?WebGLBuffer} */
    let boundBuffer = null;

    const renderConfig = config;
    const subscription = new Subscription();
    const bufferSizes = {};
    const hostBuffers = {};

    let state = {
        config: renderConfig,
        canvas: canvas,

        gl: undefined,
        ext: undefined,
        programs: {},
        buffers: {},
        bufferSizes: bufferSizes,

        //TODO make immutable
        hostBuffers: hostBuffers,
        // Non-replay subject because in our animationFrame loop,
        // we can't use RxJS
        hostBuffersCache: {},
        camera: undefined,

        //{item -> gl obj}
        textures: {},
        fbos: {},
        renderBuffers: {},
        pixelreads: {},
        uniforms: config.uniforms,
        options: config.options,
        items: config.items,

        boundBuffer: undefined,
        bufferSize: {},
        numElements: {},
        flags: { interpolateMidPoints: true },

        //keyed on instancing: 1 -> vertex, 2 -> line, 3 -> triangle, ...
        indexHostBuffers: {}, // {Uint32Array}
        indexGlBuffers: {}, // {GlBuffer}

        activeIndices: config.modes,
    };

    subscription.add(
        Observable.fromEvent(canvas, 'webglcontextlost').subscribe(() =>
            console.error('WebGL Context Loss')
        )
    );

    const pixelRatio = urlParams.pixelRatio || 2;
    const gl = createContext(state, pixelRatio);
    state.gl = gl;

    if (!gl) {
        //trust UI to do the friendly error
        console.error("Cannot initialize renderer due to no GL");

        function stubFns(o, names) {
            names.forEach((name) => o[name] = function () {});
            return o;
        }

        return {
            state: {
                ...state,
                camera: stubFns({}, ['setEdgeScaling', 'setPointScaling', 'isCenteredOn', 'getBounds', 'canvas2ScreenCoords']) 
            },
            renderer: stubFns(
              {}, 
              ['render', 'setFlags', 'setCamera', 'loadBuffer', 'loadBuffers', 'loadTextures',
               'resizeCanvas', 'getBufferNames', 'setNumElements', 'updateIndexBuffer',
               'allocateBufferSize', 'copyCanvasToTexture', 'setupFullscreenBuffer',
               'unsubscribe'])
        };

    }

    resizeCanvas(state);
    setGlOptions(gl, state.options);

    state.programs = createPrograms(config, gl);
    state.buffers = createBuffers(config, gl, bufferSizes, hostBuffers);

    // debug('precreated %j', state);
    const camera = createCamera(state, pixelRatio, urlParams);
    state.camera = camera;
    setCamera(state);

    // debug('state pre %j', state);

    const dataTextures = createNamedTextures(gl, getDataTextureNames(renderConfig));
    const { fbos, textures, renderBuffers, pixelreads } = createRenderTargets(
        state,
        renderConfig,
        config.targets,
        canvas,
        gl,
        camera
    );

    state = {
        ...state,
        fbos,
        renderBuffers,
        pixelreads,
        textures: { ...dataTextures, ...textures }
    };

    // debug('state pre b %j', state);

    // debug('created %j', state);

    return {
        state,
        renderer: {
            render,
            setFlags,
            setCamera,
            loadBuffer,
            loadBuffers,
            loadTextures,
            resizeCanvas,
            getBufferNames,
            setNumElements,
            updateIndexBuffer,
            allocateBufferSize,
            copyCanvasToTexture,
            setupFullscreenBuffer,
            unsubscribe: subscription.unsubscribe.bind(subscription)
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    // Internal helpers
    ////////////////////////////////////////////////////////////////////////////////

    /*
     * Update the size of the canvas to match what is visible
     */
    function resizeCanvas(state) {
        
        const canvas = state && state.canvas;
        const camera = state && state.camera;

        if (!canvas || !camera) {
            return;
        }

        //Note that the size is *floored*, not *rounded*
        const { maxWidth, maxHeight } = state;
        const width = Math.min(maxWidth, Math.floor(canvas.clientWidth * camera.pixelRatio));
        const height = Math.min(maxHeight, Math.floor(canvas.clientHeight * camera.pixelRatio));

        // debug('Resize: old=(%d,%d) new=(%d,%d)', canvas.width, canvas.height, width, height);
        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;

            if (camera !== undefined) {
                camera.resize(width, height, camera.pixelRatio);
                setCamera(state);
                render(state, 'resizeCanvas', 'renderSceneFull');
                render(state, 'resizeCanvas', 'picking');
                copyCanvasToTexture(state, 'steadyStateTexture');
            }
        }
    }

    //Factory behind getAttribLocationFast, getUniformLocationFast
    function addressMemoizer(cache, cacheName, glLocationMethodName) {
        return function(gl, program, programName, address) {
            if (cache[programName] !== undefined && cache[programName][address] !== undefined) {
                // debug('  Get %s %s: using fast path', cacheName, address);
                return cache[programName][address];
            }

            // debug('  Get %s %s (for %s): using slow path', cacheName, address, programName);
            cache[programName] = cache[programName] || {};
            cache[programName][address] = gl[glLocationMethodName](program, address);
            if (cache[programName][address] === -1) {
                //-1 = linker-eliminated attribute (driver-dependent, e.g. pointdepth's
                //fragment never reads pointColor): skip, warn once so typos stay visible
                console.warn(
                    'inactive address ' + cacheName + '::' + programName + '::' + address + ' -- skipping'
                );
            }

            return cache[programName][address];
        };
    }

    function useProgram(gl, program, programName) {
        if (activeProgram !== program) {
            // debug(`Use program ${programName}: on slow path`);
            gl.useProgram(program);
            activeProgram = program;
            return true;
        }
        // debug(`Use program ${programName}: on fast path`);

        return false;
    }

    function bindBuffer(gl, glArrayType, buffer) {
        if (boundBuffer !== buffer) {
            gl.bindBuffer(glArrayType, buffer);
            boundBuffer = buffer;
            return true;
        }
        return false;
    }

    /**
     * Wrapper functions to keep track of what attrib arrays are enabled.
     * Apparently WebGL isn't supposed to really care if you do this or not,
     * but documentation is lacking and it seems to be the source of
     * major performance issues.
     */
    function disableActiveVertexAttribArrays(gl) {
        _.each(enabledVertexAttribArrayCache, location => {
            gl.disableVertexAttribArray(location);
        });
        enabledVertexAttribArrayCache = [];
    }

    function enableVertexAttribArray(gl, location) {
        enabledVertexAttribArrayCache.push(location);
        gl.enableVertexAttribArray(location);
    }

    /**
     * Binds all of a programs attributes to elements of a/some buffer(s)
     * @param {WebGLRenderingContext} gl - the WebGL context containing the program and buffers
     * @param {WebGLProgram} program - The WebGL program to bind
     * @param {Object} bindings - The config settings object for this program's attributes and uniforms
     * @param {Object.<string, WebGLBuffer>} buffers - Mapping of created buffer names to WebGL buffers
     * @param {Object} modelSettings - The 'models' object from the rendering config
     */
    function bindProgram(state, program, programName, itemName, bindings, buffers, modelSettings) {
        bindings = bindings || {};
        bindings.attributes = bindings.attributes || {};
        bindings.uniforms = bindings.uniforms || {};

        const gl = state.gl;
        const uniforms = state.uniforms;
        const indexGlBuffers = state.indexGlBuffers;

        // debug('Binding program %s', programName);

        disableActiveVertexAttribArrays(gl);

        useProgram(gl, program, programName);

        _.each(bindings.attributes, (binding, attribute) => {
            const element = modelSettings[binding[0]][binding[1]];
            const glArrayType = element.index ? 'ELEMENT_ARRAY_BUFFER' : 'ARRAY_BUFFER';
            const datasource = element.datasource;
            const glBuffer = datasource === 'HOST'         ? buffers[binding[0]]
                           : datasource === 'DEVICE'       ? buffers[binding[0]]
                           : datasource === 'VERTEX_INDEX' ? indexGlBuffers[1]
                           : datasource === 'EDGE_INDEX'   ? indexGlBuffers[2]
                           : datasource === 'CLIENT'       ? buffers[binding[0]]
                           : (() => { throw new Error('unknown datasource ' + datasource); })();

            const location = getAttribLocationFast(gl, program, programName, attribute);
            if (location === -1) {
                //linker-eliminated attribute (see addressMemoizer): nothing to bind
                return;
            }
            // debug(`  binding buffer { attribute: ${attribute}, binding: ${JSON.stringify(binding)}, datasource: ${datasource}, glArrayType: ${glArrayType}, glBuffer: ${glBuffer}, element: ${JSON.stringify(element)} }`);
            bindBuffer(gl, gl[glArrayType], glBuffer);
            if (element.integer) {
                //integer attributes (e.g. edgeEndpointIds) must use the I-pointer:
                //vertexAttribPointer would float-convert and `in uint` reads garbage
                gl.vertexAttribIPointer(location, element.count, gl[element.type], element.stride, element.offset);
            } else {
                gl.vertexAttribPointer(location, element.count, gl[element.type], element.normalize, element.stride, element.offset);
            }
            enableVertexAttribArray(gl, location);
            // Divisor is per-location context state that survives rebinds; locations are
            // shared across programs, so reset it unconditionally rather than only when set.
            gl.vertexAttribDivisor(location, element.divisor || 0);
        });

        _.each(bindings.uniforms || {}, (binding, uniformName) => {
            // debug(`  binding uniform { binding: ${binding}, uniformName: ${uniformName} }`);

            const location = getUniformLocationFast(gl, program, programName, uniformName);
            const values = uniforms[itemName][uniformName];
            gl['uniform' + binding.uniformType].apply(gl, [location].concat(values));
        });

        //each sampler binds the scene texture of the same name
        _.each(bindings.textures || [], (textureName) => {
            const location = getUniformLocationFast(gl, program, programName, textureName);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, state.textures[textureName]);
            gl.uniform1i(location, 0);
        });
    }

    function setFlags(state, name, bool) {
        const flags = state.flags;
        flags[name] = bool;
    }

    // function validateNoneOfTheArgsAreUndefined(functionName, args) {
    //     const webglDebug = require('webgl-debug');
    //     for (var ii = 0; ii < args.length; ++ii) {
    //         if (args[ii] === undefined) {
    //             console.error(`undefined passed to gl.${functionName}(${webglDebug.glFunctionArgsToString(functionName, args)})`);
    //         }
    //     }
    // } 
      
    // function throwOnGLError(err, funcName, args) {
    //     const webglDebug = require('webgl-debug');
    //     const message = webglDebug.glEnumToString(err) + " was caused by call to: " + funcName;
    //     try {
    //         throw new Error(message);
    //     } catch (e) {
    //         console.log(`lastRenderTarget: ${lastRenderTarget}`);
    //         if (e) {
    //             console.log(e.message);
    //             console.log(e.stack || e.toString());
    //         }
    //     }
    // }

    function createContext(state, pixelRatio) {
        const canvas = state.canvas;
        const aa = pixelRatio <= 1; // Disable AA on retina display
        const glOptions = { antialias: aa, premultipliedAlpha: true };
        const gl = canvas.getContext('webgl2', glOptions);
        if (gl === null) {
            console.error('Could not initialize WebGL 2');
            //UI will warn user
            return;
        }
        // if (process.env.NODE_ENV === 'debug') {
        //     const webglDebug = require('webgl-debug');
        //     gl = webglDebug.makeDebugContext(gl, throwOnGLError, validateNoneOfTheArgsAreUndefined);
        // }
        const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        const [maxWidth, maxHeight] = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
        state.maxWidth = maxWidth / 2.0;
        state.maxHeight = maxHeight / 2.0;
        state.maxTextureSize = maxTextureSize;
        console.info('gl max_viewport_dims', { maxWidth, maxHeight });
        // use `gl.drawingBufferWidth` and `gl.drawingBufferHeight` instead of `canvas.width` or `canvas.height`:
        // https://webgl2fundamentals.org/webgl/lessons/webgl-anti-patterns.html
        gl.viewport(0, 0,
            Math.min(state.maxWidth, gl.drawingBufferWidth),
            Math.min(state.maxHeight, gl.drawingBufferHeight)
        );

        return gl;
    }

    function createCamera(state, pixelRatio, urlParams = {}) {
        const gl = state.gl;
        
        const camConfig = state.config.camera;
        const drawingBufferWidth = gl.drawingBufferWidth;
        const drawingBufferHeight = gl.drawingBufferHeight;

        let bounds = camConfig.bounds || {};

        if (bounds === 'CANVAS') {
            bounds = {
                left: 0,
                right: drawingBufferWidth,
                top: 0,
                bottom: drawingBufferHeight
            };
        } else {
            const {
                top = bounds.top,
                left = bounds.left,
                right = bounds.right,
                bottom = bounds.bottom
            } = urlParams;
            bounds = { top, left, right, bottom };
        }

        /** Allow &camera=3d to override camera type (defaults to 2d); currently deprecated/ignored. */
        const mode = urlParams.camera
            ? urlParams.camera === '3d' ? '3d' : '2d'
            : camConfig.type === '3d' ? '3d' : '2d';

        const camera = new Camera2d(
            bounds.left,
            bounds.right,
            bounds.top,
            bounds.bottom,
            camConfig.nearPlane,
            camConfig.farPlane,
            mode
        );

        console.info("Display's pixel ratio is", pixelRatio);
        camera.resize(drawingBufferWidth, drawingBufferHeight, pixelRatio);

        return camera;
    }

    /*
     * Return the items to render when no override is given to render()
     */
    function getItemsForTrigger(state, trigger) {
        if (!trigger) {
            return undefined;
        }
        const { items, triggers } = state.config;
        const itemNamesForTrigger = triggers[trigger];
        return itemNamesForTrigger.filter(itemName => {
            return !!items[itemName];
        });
    }

    // RenderState * canvas * string -> {x: int, y:int, width: float, height: float}
    function getTextureDims(state, config, canvas, camera, name) {
        const { maxWidth, maxHeight, gl } = state;
        const drawingBufferWidth = gl.drawingBufferWidth;
        const drawingBufferHeight = gl.drawingBufferHeight;

        if (!name || name === 'CANVAS') {
            return {
                width: Math.min(maxWidth, drawingBufferWidth),
                height: Math.min(maxHeight, drawingBufferHeight)
            };
        }

        const textureConfig = config.textures[name];

        //Retina-quality
        const width = textureConfig.hasOwnProperty('width')
            ? Math.floor(0.01 * textureConfig.width.value * drawingBufferWidth)
            : Math.floor(drawingBufferWidth);
        const height = textureConfig.hasOwnProperty('height')
            ? Math.floor(0.01 * textureConfig.height.value * drawingBufferHeight)
            : Math.floor(drawingBufferHeight);

        return { width: Math.min(maxWidth, width), height: Math.min(maxHeight, height) };
    }

    function initializeTexture(gl, texture, fbo, renderBuffer, width, height) {
        gl.bindTexture(gl.TEXTURE_2D, texture);

        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

        //NPOT dimensions: https://developer.mozilla.org/en-US/docs/Web/WebGL/Using_textures_in_WebGL
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.bindRenderbuffer(gl.RENDERBUFFER, renderBuffer);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.framebufferRenderbuffer(
            gl.FRAMEBUFFER,
            gl.DEPTH_ATTACHMENT,
            gl.RENDERBUFFER,
            renderBuffer
        );
    }

    // create for each texture rendertarget, an offscreen fbo, texture, renderbuffer, and host buffer
    // note that not all textures are render targets (e.g., server reads)
    function createRenderTargets(state, config, neededTextures, canvas, gl, camera) {
        const textures = neededTextures.map(gl.createTexture.bind(gl)),
            fbos = neededTextures.map(gl.createFramebuffer.bind(gl)),
            renderBuffers = neededTextures.map(gl.createRenderbuffer.bind(gl)),
            dimensions = neededTextures.map(getTextureDims.bind(undefined, state, config, canvas, camera)),
            pixelreads = dimensions.map(({ width, height }) => new Uint8Array(width * height * 4));

        // bind
        _.zip(
            textures,
            fbos,
            renderBuffers,
            dimensions
        ).forEach(([texture, fbo, renderBuffer, eachDimensions]) => {
            initializeTexture(
                gl,
                texture,
                fbo,
                renderBuffer,
                eachDimensions.width,
                eachDimensions.height
            );
        });

        return {
            textures: _.zipObject(neededTextures, textures),
            fbos: _.zipObject(neededTextures, fbos),
            renderBuffers: _.zipObject(neededTextures, renderBuffers),
            pixelreads: _.zipObject(neededTextures, pixelreads)
        };
    }

    // [ string ] -> {<name>: glTexture}
    function createNamedTextures(gl, names) {
        const textures = names.map(gl.createTexture.bind(gl));
        return _.zipObject(names, textures);
    }

    /**
     * Set global GL settings
     * @param {WebGLRenderingContext}
     */
    function setGlOptions(gl, glOpts) {
        const whiteList = {
            enable: true,
            disable: true,
            blendFuncSeparate: true,
            blendEquationSeparate: true,
            depthFunc: true,
            colorMask: true,
            lineWidth: true
        };

        _.each(glOpts || {}, (optionCalls, optionName) => {
            if (whiteList[optionName] !== true || typeof gl[optionName] !== 'function') {
                return;
            }

            optionCalls.forEach(optionArgs => {
                const newArgs = optionArgs.map(currentValue => {
                    return typeof currentValue === 'string' ? gl[currentValue] : currentValue;
                });
                gl[optionName].apply(gl, newArgs);
            });
        });
    }

    function createPrograms(config, gl) {
        // debug('Creating programs');
        return Object.keys(config.programs).reduce((programs, programName) => {
            const programOptions = config.programs[programName];
            // debug('Compiling program', programName);
            const program = gl.createProgram();

            //// Create, compile and attach the shaders
            const vertShader = gl.createShader(gl.VERTEX_SHADER);
            let sourceCode = programOptions.sources.vertex;
            let log;
            gl.shaderSource(vertShader, sourceCode);
            gl.compileShader(vertShader);
            if (
                !(gl.isShader(vertShader) && gl.getShaderParameter(vertShader, gl.COMPILE_STATUS))
            ) {
                log = gl.getShaderInfoLog(vertShader);
                // debug('Error in vertex shader', programName, log, sourceCode);
                throw new Error(
                    'Could not compile vertex shader "' + programName + '" (' + log + ')'
                );
            }
            gl.attachShader(program, vertShader);

            const fragShader = gl.createShader(gl.FRAGMENT_SHADER);
            sourceCode = programOptions.sources.fragment;
            gl.shaderSource(fragShader, sourceCode);
            gl.compileShader(fragShader);
            if (
                !(gl.isShader(fragShader) && gl.getShaderParameter(fragShader, gl.COMPILE_STATUS))
            ) {
                log = gl.getShaderInfoLog(fragShader);
                // debug('Error in fragment shader', programName, log, sourceCode);
                throw new Error(
                    'Could not compile fragment shader "' + programName + '" (' + log + ')'
                );
            }
            gl.attachShader(program, fragShader);

            //// Link and validate the program
            gl.linkProgram(program);
            if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
                console.error(
                    "Could not link program '" +
                        programName +
                        "'. Log:\n" +
                        gl.getProgramInfoLog(program)
                );
                gl.deleteProgram(program);
                throw new Error("Could not link GL program '" + programName + "'");
            }

            gl.validateProgram(program);
            if (!gl.getProgramParameter(program, gl.VALIDATE_STATUS)) {
                console.error(gl.getProgramParameter(program, gl.VALIDATE_STATUS));
                throw new Error(
                    "Could not validate GL program '" + programName + "':",
                    JSON.stringify(programOptions)
                );
            }

            programs[programName] = program;

            return programs;
        }, {});
    }

    function createBuffers(config, gl, bufferSizes, hostBuffers) {
        return Object.keys(config.models).reduce((buffers, name) => {
            const model = config.models[name];
            // debug('Creating buffer %s with options %o', name, JSON.stringify(model));
            bufferSizes[name] = 0;
            for (const key in model) {
                const { [key]: { datasource } } = model;
                if (datasource === 'HOST' || datasource === 'DEVICE') {
                    hostBuffers[name] = new ReplaySubject(1);
                }
            }
            //hostOnly models feed CPU consumers + the data-texture mirror; no
            //attribute reads them, so no GL buffer exists for them at all
            if (!_.values(model)[0].hostOnly) {
                buffers[name] = gl.createBuffer();
            }
            return buffers;
        }, {});
    }

    function uploadTexels(gl, textureNfo, format, type) {
        //default 4 rejects tightly packed rows whose byte length isn't a 4-multiple
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

        const comps = textureNfo.componentsPerTexel;
        const fullRows = (textureNfo.texelCount / textureNfo.width) | 0;
        if (fullRows > 0) {
            gl.texSubImage2D(
                gl.TEXTURE_2D, 0, 0, 0,
                textureNfo.width, fullRows,
                format, type,
                textureNfo.buffer, 0
            );
        }
        const tail = textureNfo.texelCount - fullRows * textureNfo.width;
        if (tail > 0) {
            gl.texSubImage2D(
                gl.TEXTURE_2D, 0, 0, fullRows,
                tail, 1,
                format, type,
                textureNfo.buffer, fullRows * textureNfo.width * comps
            );
        }
    }

    function loadTextures(state, bindings) {
        _.each(bindings, loadTexture.bind('', state));
    }

    function loadTexture(state, textureNfo, name) {
        // debug('load texture %s %j', name, textureNfo);

        const gl = state.gl;

        const texture = state.textures[name];
        // debug('  got texture %j', texture);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, !textureNfo.dataTexture);
        const internalFormat = textureNfo.internalFormat
            ? gl[textureNfo.internalFormat]
            : gl.RGBA;
        const format = textureNfo.format ? gl[textureNfo.format] : gl.RGBA;
        const type = textureNfo.type ? gl[textureNfo.type] : gl.UNSIGNED_BYTE;
        // In-place update fast path: for data textures, when the
        // texture is already allocated at this exact size/format, upload with
        // texSubImage2D to skip the storage reallocation texImage2D does every
        // call -- the difference matters for large data textures re-uploaded per
        // frame during layout (see the scheduler's syncDataTextures).
        const allocs = state.textureAllocations || (state.textureAllocations = {});
        const allocKey = `${textureNfo.width}x${textureNfo.height}x${internalFormat}x${format}x${type}`;

        if (textureNfo.dataTexture) {
            if (allocs[name] !== allocKey) {
                gl.texImage2D(
                    gl.TEXTURE_2D, 0, internalFormat,
                    textureNfo.width, textureNfo.height, 0,
                    format, type, null
                );
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
                allocs[name] = allocKey;
            }
            uploadTexels(gl, textureNfo, format, type);
        } else {
            gl.texImage2D(
                gl.TEXTURE_2D,
                0,
                internalFormat,
                textureNfo.width,
                textureNfo.height,
                0,
                format,
                type,
                textureNfo.buffer
            );
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_NEAREST);
            gl.generateMipmap(gl.TEXTURE_2D);
            allocs[name] = allocKey;
        }
        gl.bindTexture(gl.TEXTURE_2D, null);
    }

    /**
     * Given an object mapping buffer/texture names to ArrayBuffer data, load all of them into the GL context
     * @param {RendererState} state - renderer instance
     * @param {Object.<string, ArrayBuffer>} bufferData - a mapping of buffer name -> new data to load
     * into that buffer
     * @returns {Boolean} success flag
     */
    function loadBuffers(state, bufferData) {
        const config = state.config;
        const buffers = state.buffers;

        _.each(bufferData, (dataOrBufferNFO, bufferName) => {
            let partial = dataOrBufferNFO.partial || false;
            let byteOffset = dataOrBufferNFO.partial ? dataOrBufferNFO.byteOffset : 0;
            let data = partial ? dataOrBufferNFO.data : dataOrBufferNFO.data || dataOrBufferNFO;

            // debug('Loading buffer data for buffer %s (data type: %s, length: %s bytes)', bufferName, data.constructor.name, data.byteLength);

            const model = _.values(config.models[bufferName])[0];
            if (model === undefined) {
                // console.error(
                //     "Asked to load data for buffer '%s', but corresponding model not found",
                //     bufferName
                // );
                // console.log('models: ', config.models);
                return;// false;
            }

            //hostOnly: skip the GL upload entirely (positions reach the GPU
            //solely through syncDataTextures); the host-cache block below is
            //what CPU consumers (labels, gestures, expansion) live on
            if (!model.hostOnly) {
                if (typeof buffers[bufferName] === 'undefined') {
                    // console.error(
                    //     "Asked to load data for buffer '%s', but no buffer by that name exists locally",
                    //     bufferName,
                    //     buffers
                    // );
                    return;// false;
                }
                loadBuffer(state, buffers[bufferName], bufferName, model, data, byteOffset);
            }

            if (model.datasource === 'HOST' || model.datasource === 'DEVICE') {
                let hostBuf = state.hostBuffersCache[bufferName];
                if (!partial || !hostBuf) {
                    state.hostBuffersCache[bufferName] = hostBuf = data;
                } else if ((byteOffset + data.byteLength) > hostBuf.byteLength) {
                    const Ctor = hostBuf.constructor;
                    const BPE = Ctor.BYTES_PER_ELEMENT;
                    const newArr = new Ctor((byteOffset + data.byteLength) / BPE);
                    arrayBufferMemcpy(newArr, hostBuf, 0);
                    arrayBufferMemcpy(newArr, data, byteOffset);
                    state.hostBuffersCache[bufferName] = hostBuf = newArr;
                } else {
                    arrayBufferMemcpy(hostBuf, data, byteOffset);
                }
                state.hostBuffers[bufferName].onNext(hostBuf);
            }
            return true;
        });
    }

    // Eagerly allocate a buffer for a given buffer name if one of that size
    // doesn't already exist
    function allocateBufferSize(state, bufferName, sizeInBytes) {
        const gl = state.gl;
        const config = state.config;
        const buffers = state.buffers;
        const bufferSizes = state.bufferSizes;

        const buffer = buffers[bufferName];
        const model = _.values(config.models[bufferName])[0];
        const glArrayType = model.index ? gl.ELEMENT_ARRAY_BUFFER : gl.ARRAY_BUFFER;
        const glHint = model.hint || 'STREAM_DRAW';

        if (sizeInBytes > bufferSizes[bufferName]) {
            bindBuffer(gl, glArrayType, buffer);
            gl.bufferData(glArrayType, sizeInBytes, gl[glHint]);
            bufferSizes[bufferName] = sizeInBytes;
        }
    }

    function loadBuffer(state, buffer, bufferName, model, data, glByteOffset = 0) {
        const gl = state.gl;
        const bufferSizes = state.bufferSizes;

        if (typeof bufferSizes[bufferName] === 'undefined') {
            console.error('loadBuffer: No size for buffer', bufferName);
            return;
        }
        if (data.byteLength <= 0) {
            // console.warn('loadBuffer: Asked to load data for buffer \'%s\', but data length is 0', bufferName);
            return;
        }

        try {
            loadBufferBody(state, buffer, bufferName, model, data, bufferSizes, gl, glByteOffset);
        } catch (glErr) {
            // This often doesn't get called on GL errors, since they seem to be thrown from the global
            // WebGL context, not at the point we call the command (above).
            console.error('Error: could not load data into buffer', bufferName, '. Error:', glErr);
            throw glErr;
        }
    }

    // Separated out because V8 can't optimize try-catch bodies.
    function loadBufferBody(state, buffer, bufferName, model, data, bufferSizes, gl, byteOffset = 0) {
        const glArrayType = model.index ? gl.ELEMENT_ARRAY_BUFFER : gl.ARRAY_BUFFER;
        const glHint = model.hint || 'STREAM_DRAW';
        bindBuffer(gl, glArrayType, buffer);

        if (bufferSizes[bufferName] >= (byteOffset + data.byteLength)) {
            // debug('Reusing existing GL buffer to load data for buffer %s (current size: %d, byteOffset: %d, new data size: %d)', bufferName, bufferSizes[bufferName], byteOffset, data.byteLength);
            gl.bufferSubData(glArrayType, byteOffset, data);
        } else {
            // debug('Creating new buffer data store for buffer %s (new size: %d, hint: %s)', bufferName, data.byteLength, glHint);
            gl.bufferData(glArrayType, data, gl[glHint]);
            bufferSizes[bufferName] = byteOffset + data.byteLength;
        }
    }

    //Create new index array that extends old one
    //GLContext * int * int * UInt32Array -> Uint32Array
    function expandHostBuffer(gl, length, repetition, oldHostBuffer) {
        const longerBuffer = new Uint32Array(Math.round(length * repetition));

        //memcpy old (initial) indexes
        if (oldHostBuffer.length) {
            longerBuffer.set(oldHostBuffer);
        }

        // 31-bit encoding: use bits 0-30 for index, bit 31 for dimension flag
        // Repetition of 1 (points): bit 31 = 0
        // Repetition of 2 (edges): bit 31 = 1
        const DIMENSION_BIT = 1 << 31;
        const dimensionFlag = repetition === 2 ? DIMENSION_BIT : 0;

        let unshiftedIndex = oldHostBuffer.length / repetition + 1;
        for (let i = oldHostBuffer.length; i < longerBuffer.length; i += repetition) {
            // Encode: index in bits 0-30, dimension flag in bit 31
            const encodedValue = unshiftedIndex | dimensionFlag;

            for (let j = 0; j < repetition; j++) {
                longerBuffer[i + j] = encodedValue;
            }

            unshiftedIndex++;
        }

        return longerBuffer;
    }

    function updateIndexBuffer(state, length, repetition) {
        const gl = state.gl;
        const indexHostBuffers = state.indexHostBuffers;
        const indexGlBuffers = state.indexGlBuffers;

        length = Math.ceil(length);

        if (!indexHostBuffers[repetition]) {
            indexHostBuffers[repetition] = new Uint32Array([]);
        }
        if (!indexGlBuffers[repetition]) {
            indexGlBuffers[repetition] = gl.createBuffer();
            indexGlBuffers[repetition].repetition = repetition;
        }

        const oldHostBuffer = indexHostBuffers[repetition];

        if (oldHostBuffer.length < length * repetition) {
            const longerBuffer = expandHostBuffer(
                gl,
                length,
                repetition,
                indexHostBuffers[repetition]
            );
            indexHostBuffers[repetition] = longerBuffer;

            const glBuffer = indexGlBuffers[repetition];

            // debug('Expanding index buffer', glBuffer, 'memcpy', oldHostBuffer.length / repetition, 'elts', 'write to', length * repetition);

            bindBuffer(gl, gl.ARRAY_BUFFER, glBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, longerBuffer, gl.STATIC_DRAW);
        }
    }

    /** A mapping of scene items to the number of elements that should be rendered for them */
    function setNumElements(state, item, newNumElements) {
        state.numElements[item] = newNumElements;
    }

    function setUniform(state, name, value) {
        const uniforms = state.uniforms;
        _.each(uniforms, map => {
            if (name in map) {
                map[name] = value;
            }
        });
    }

    function setCamera(state) {
        const config = state.config;
        const gl = state.gl;
        const programs = state.programs;
        const uniforms = state.uniforms;
        const camera = state.camera;
        // TODO: Actually get number of nodes from the server
        const numVertices = state.numElements.pointculled || 0;

        // Set zoomScalingFactor uniform if it exists.
        _.each(uniforms, map => {
            if ('zoomScalingFactor' in map) {
                map['zoomScalingFactor'] = camera.semanticZoom(numVertices);
                // debug(`setting uniform zoomScalingFactor to "${map['zoomScalingFactor']}"`);
            }

            if ('edgeZoomScalingFactor' in map) {
                map['edgeZoomScalingFactor'] = camera.semanticZoomEdges(numVertices);
                // debug(`setting uniform edgeZoomScalingFactor to "${map['edgeZoomScalingFactor']}"`);
            }

            if ('maxScreenSize' in map) {
                map['maxScreenSize'] = Math.max(camera.width, camera.height);
                // debug(`setting uniform maxScreenSize to "${map['maxScreenSize']}"`);
            }

            if ('maxCanvasSize' in map) {
                map['maxCanvasSize'] = Math.max(gl.drawingBufferWidth, gl.drawingBufferHeight);
                // debug(`setting uniform maxCanvasSize to "${map['maxCanvasSize']}"`);
            }

            if ('viewportSize' in map) {
                map['viewportSize'] = [gl.drawingBufferWidth, gl.drawingBufferHeight];
            }
        });

        //HACK: we should have line shaders, and pass this as a uniform
        if (numVertices) {
            gl.lineWidth(camera.semanticZoomEdges(numVertices));
        }

        _.each(config.programs, (programConfig, programName) => {
            // debug('Setting camera for program %s', programName);
            const program = programs[programName];
            useProgram(gl, program, programName);

            const mvpLoc = getUniformLocationFast(gl, program, programName, programConfig.camera);
            gl.uniformMatrix4fv(mvpLoc, false, camera.getMatrix());
        });
    }

    //if maybeDims provided, also resize texture if needed
    function updateRenderTarget(state, renderTarget, maybeDims) {
        const gl = state.gl;

        //null, undefined and 'CANVAS' are one key (the default framebuffer):
        //a redundant rebind of it opens a canvas frame Firefox then waits on
        renderTarget = renderTarget && renderTarget !== 'CANVAS' ? renderTarget : null;

        const isChangedTarget = renderTarget !== lastRenderTarget;

        const isMaybeResized = renderTarget && maybeDims;
        let isResized;
        if (isMaybeResized) {
            const targetDims = state.renderTargetDims || (state.renderTargetDims = {});
            const current = targetDims[renderTarget];
            isResized = !current ||
                current.width !== maybeDims.width ||
                current.height !== maybeDims.height;
            if (isResized) {
                targetDims[renderTarget] = { width: maybeDims.width, height: maybeDims.height };
            }
        }

        if (isResized) {
            // debug('resizing texture', renderTarget, maybeDims.width, maybeDims.height);
            const textures = state.textures;
            const texture = textures[renderTarget];
            const fbos = state.fbos;
            const fbo = fbos[renderTarget];
            const renderBuffers = state.renderBuffers;
            const renderBuffer = renderBuffers[renderTarget];

            initializeTexture(gl, texture, fbo, renderBuffer, maybeDims.width, maybeDims.height);
        }

        if (isChangedTarget || isResized) {
            // debug('  rebinding renderTarget', renderTarget);
            gl.bindFramebuffer(gl.FRAMEBUFFER, renderTarget ? state.fbos[renderTarget] : null);
            lastRenderTarget = renderTarget;
        }
    }

    function copyCanvasToTexture(state, textureName) {
        const gl = state.gl;
        updateRenderTarget(state, null);
        gl.bindTexture(gl.TEXTURE_2D, state.textures[textureName]);
        gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 0, 0,
            gl.drawingBufferWidth, gl.drawingBufferHeight, 0);
    }

    function setupFullscreenBuffer(state) {
        const fullscreenBuffer = new Float32Array([
            1.0, 1.0,
            -1.0, 1.0,
            -1.0, -1.0,
            -1.0, -1.0,
            1.0, -1.0,
            1.0, 1.0
        ]);

        const numFullscreenElements = 6;

        setNumElements(state, 'fullscreen', numFullscreenElements);
        setNumElements(state, 'fullscreenDark', numFullscreenElements);
        loadBuffers(state, { fullscreenCoordinates: fullscreenBuffer });
    }

    /**
     * Render one or more items as specified in render config's render array
     * Implemented by queueing up a rendering task, which will lazilly be handled by
     * the animation loop of the browser.
     * @param {Immutable} state - initialized renderer
     * @param {String} tag - Extra information to track who requested rendering for debugging/perf
     * @param {String} renderListTrigger - The trigger tag of the render items to execute
     * @param {[String]} renderListOverride - List of render items to execute
     * @param {Object} readPixelsOverride - Dimensions for readPixels
     * @param {Function<Boolean>} callback - Callback executed after readPixels
     */
    function render(
        state,
        tag,
        renderListTrigger,
        renderListOverride,
        readPixelsOverride,
        callback
    ) {
        const config = state.config,
            camera = state.camera,
            gl = state.gl,
            programs = state.programs,
            numElements = state.numElements,
            buffers = state.buffers;

        // const toRender = getItemsForTrigger(state, renderListTrigger) || renderListOverride;
        const toRender = renderListOverride || getItemsForTrigger(state, renderListTrigger);
        if (toRender === undefined || toRender.length === 0) {
            console.warn('Nothing to render for tag', tag);
            if (callback) {
                callback(false);
            }
            return;
        }

        // console.group(`==== Rendering a frame (tag: ${tag})`);

        // Update index buffers based on largest currently loaded buffer.
        const maxElements = Math.max(_.max(_.values(numElements)), 0);
        state.activeIndices.forEach(updateIndexBuffer.bind('', state, maxElements));

        const itemToTarget = function(itemName) {
            const itemDef = config.items[itemName];
            if (!itemDef) {
                console.error('Trying to render unknown item', itemName);
            }
            return itemDef.renderTarget === 'CANVAS' ? null : itemDef.renderTarget;
        };

        const sortedItems = toRender.slice();
        sortedItems.sort((a, b) => {
            const aTarget = itemToTarget(a);
            const bTarget = itemToTarget(b);
            return aTarget < bTarget ? -1 : aTarget === bTarget ? 0 : 1;
        });

        const clearedFBOs = {};
        let numTexturesToRead = 0;
        const texturesToRead = Object.create(null);

        //size readback targets before the draw loop: a (re)alloc clears the
        //texture, so it must precede the fill -- between draw and readback it reads blank
        sortedItems.forEach(item => {
            const itemDef = config.items[item];
            const target = itemDef && itemDef.renderTarget;
            if (target && target !== 'CANVAS' && itemDef.readTarget) {
                const dims = getTextureDims(state, config, canvas, camera, target);
                updateRenderTarget(state, target,
                    readPixelsOverride || { x: 0, y: 0, width: dims.width, height: dims.height });
            }
        });

        sortedItems.forEach(item => {
            const texture = renderItem(state, config, camera, gl, programs, buffers, clearedFBOs, item);
            if (texture) {
                ++numTexturesToRead;
                texturesToRead[texture] = true;
            }
        });

        if (numTexturesToRead > 0) {
            const pixelreads = state.pixelreads;
            //sentinel entry keeps the count from hitting zero until every read
            //below has been started (the no-fence fallback completes synchronously)
            let pendingReads = 1;
            const onReadDone = () => {
                if (--pendingReads === 0 && callback) {
                    callback(true);
                }
            };
            for (const renderTarget in texturesToRead) {
                // debug(`reading back texture ${renderTarget}`);

                let texture = pixelreads[renderTarget];
                const dims = getTextureDims(state, config, canvas, camera, renderTarget);
                const readDims = readPixelsOverride || { x: 0, y: 0, width: dims.width, height: dims.height };

                updateRenderTarget(state, renderTarget, readDims);

                if (!texture || texture.length !== readDims.width * readDims.height * 4) {
                    // debug(`reallocating texture "${renderTarget}" buffer: %d bytes, dims: %d`, texture.length, readDims.width * readDims.height * 4);
                    texture = new Uint8Array(readDims.width * readDims.height * 4);
                }

                // A synchronous gl.readPixels would stall the main thread until
                // the GPU drained every queued command (seconds on huge graphs);
                // the async path fences and calls back once data has landed.
                ++pendingReads;
                startAsyncReadPixels(state, renderTarget, readDims, texture, () => {
                    //publish only once data has landed: a pixelreads entry
                    //means "latest completed readback" (hitTestN treats
                    //presence as validity; a settle deletes stale entries)
                    pixelreads[renderTarget] = texture;
                    onReadDone();
                });
            }
            //rebind the default framebuffer while idle (deferred to a gesture's
            //first frame it stalls Firefox ~650ms); a settle must draw the scene
            //between this rebind and its steady-state copy, and never present
            //a no-draw frame -- that shows a cleared backbuffer
            updateRenderTarget(state, null);
            gl.flush();
            onReadDone();   //release the sentinel
            return;
        }

        gl.flush();

        if (callback) {
            callback(true);
        }
        // console.groupEnd();
    }

    /**
     * Non-blocking readPixels: read into a PIXEL_PACK_BUFFER, fence, and poll
     * until the GPU signals, then copy into outArray and call onDone. The
     * newest request per target wins: it always issues a real read (its
     * buffer is the one registered in state.pixelreads -- dropping it left an
     * all-zero hitmap and dead hover); a superseded in-flight poll
     * self-cancels via the generation counter and reports stale-done.
     */
    function startAsyncReadPixels(state, renderTarget, readDims, outArray, onDone) {
        const gl = state.gl;
        const generations = state.pixelReadGenerations || (state.pixelReadGenerations = {});
        const generation = generations[renderTarget] = (generations[renderTarget] || 0) + 1;

        const pbos = state.pixelPackBuffers || (state.pixelPackBuffers = {});
        const pbo = pbos[renderTarget] || (pbos[renderTarget] = gl.createBuffer());

        //bufferData re-specs (orphans) the PBO, so a superseded in-flight read
        //on the same PBO is safe -- its poll exits on the generation check
        //without ever touching the storage
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo);
        gl.bufferData(gl.PIXEL_PACK_BUFFER, outArray.byteLength, gl.STREAM_READ);
        gl.readPixels(readDims.x, readDims.y, readDims.width, readDims.height, gl.RGBA, gl.UNSIGNED_BYTE, 0);
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);

        const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
        if (!sync) {
            //fence unavailable: fall back to a synchronous read
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo);
            gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, outArray);
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
            onDone();
            return true;
        }

        const poll = () => {
            if (generations[renderTarget] !== generation) {
                gl.deleteSync(sync);
                onDone();
                return;
            }
            const status = gl.clientWaitSync(sync, 0, 0);
            if (status === gl.ALREADY_SIGNALED || status === gl.CONDITION_SATISFIED) {
                gl.deleteSync(sync);
                gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo);
                gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, outArray);
                gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
                onDone();
            } else if (status === gl.WAIT_FAILED) {
                gl.deleteSync(sync);
                onDone();
            } else {
                setTimeout(poll, 4);
            }
        };
        setTimeout(poll, 0);
        return true;
    }

    // Helper to force GL Finish for debugging.
    // const stubTexture = new Uint8Array(5 * 5 * 4);
    // function readPixelStub(state) {
    //     const gl          = state.get('gl');
    //     const renderTarget = 'pointHitmapDownsampled';
    //     updateRenderTarget(state, renderTarget);

    //     const pixelreads = state.get('pixelreads');
    //     const texture = stubTexture;
    //     const readDims = {x: 0, y: 0, width: 5, height: 5};

    //     gl.readPixels(readDims.x, readDims.y, readDims.width, readDims.height,
    //                     gl.RGBA, gl.UNSIGNED_BYTE, texture);
    // }

    function setTextureUniforms(state, config, itemDef, renderTarget) {
        if (!renderTarget || renderTarget === 'CANVAS') {
            return;
        }

        const uniformsToSet = config.textures[renderTarget].uniforms;
        if (uniformsToSet) {
            _.each(uniformsToSet, (val, key) => {
                setUniform(state, key, val);
            });
        }
    }

    function renderItem(
        state,
        config,
        camera,
        gl,
        programs,
        buffers,
        clearedFBOs,
        item
    ) {
        const itemDef = config.items[item];
        const numElements = state.numElements[item] || 0;
        const renderTarget = itemDef.renderTarget === 'CANVAS' ? null : itemDef.renderTarget;

        // debugRenderItem('Rendering item "%s" (%d elements)', item, numElements);

        const textureToRead =
            renderTarget !== null && itemDef.readTarget ? renderTarget : undefined;

        //change viewport in case of downsampled target
        const dims = getTextureDims(state, config, canvas, camera, renderTarget);

        if ((dims.width * dims.height) <= 0) { return; }

        updateRenderTarget(state, renderTarget, dims);
        gl.viewport(0, 0, dims.width, dims.height);

        //set uniforms for downsampled targets
        setTextureUniforms(state, config, itemDef, renderTarget);

        if (!clearedFBOs[renderTarget]) {
            // debugRenderItem(`  clearing render target ${renderTarget}`);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
            clearedFBOs[renderTarget] = true;
        }

        // This check is placed after clearing targets, but right before the program
        // would be bound and drawn. This way we capture the side effects of attempting
        // to render an item, while still skipping what we can.
        if (typeof numElements === 'undefined' || numElements === 0) {
            // debugRenderItem('Not rendering item "%s" because it doesn\'t have a non-zero numElements', item);
            return textureToRead;
        }

        setGlOptions(
            gl,
            _.omit(_.extend({}, state.options, itemDef.glOptions), 'clearColor', 'lineWidth')
        );

        const depthFunc = ((itemDef.glOptions || {}).depthFunc || config.options.depthFunc)[0][0];
        gl.depthFunc(gl[depthFunc]);

        bindProgram(
            state,
            programs[itemDef.program],
            itemDef.program,
            item,
            {
                attributes: itemDef.bindings,
                uniforms: itemDef.uniforms,
                textures: config.programs[itemDef.program].textures
            },
            buffers,
            config.models
        );

        // debugRenderItem(`Done binding, drawing type "${itemDef.drawType} (${gl[itemDef.drawType]})" now...`);
        if (itemDef.instanced) {
            // numElements is the instance count for instanced items
            gl.drawArraysInstanced(gl[itemDef.drawType], 0, itemDef.verticesPerInstance, numElements);
        } else {
            gl.drawArrays(gl[itemDef.drawType], 0, numElements);
        }

        return textureToRead;
    }

    // Get list of live buffers, where live means referenced by a renderitem
    // Optional filter predicate
    // RenderConfig * ?(ModelView -> Bool) -> [ String ]
    function getBufferNames(config, optFilter) {
        const filterPred = optFilter || (() => true);

        const renderItems = config.render;
        const bufferNamesLists = renderItems.map(itemName => {
            const iDef = config.items[itemName];
            const bindings = _.values(iDef.bindings);
            const elementIndex = iDef.index ? [iDef.index] : [];
            const otherBuffers = _.values(iDef.otherBuffers);
            const bufferList = bindings.concat(otherBuffers).concat(elementIndex);
            return bufferList
                .filter(binding => {
                    const modelName = binding[0];
                    const attribName = binding[1];
                    return filterPred(config.models[modelName][attribName]);
                })
                .map(binding => {
                    const modelName = binding[0];
                    return modelName;
                });
        });

        return _.uniq(_.flatten(bufferNamesLists));
    }

    // (Client-computed data textures: referenced by the scene but not
    // render targets; classified by generateScene)
    // RenderOptions -> [ string ]
    function getDataTextureNames(config) {
        return config.dataTextures || [];
    }
}
